import axios from 'axios'
import { ElLoading, ElMessageBox } from 'element-plus'
import router from '../router'

const baseURL = process.env.VUE_APP_BASE_URL
// 创建axios实例
const service = axios.create({
  baseURL: baseURL,
  timeout: 30000, // 默认超时时间 30秒
  // 优化CORS请求，减少OPTIONS预检请求
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})
let loading

// 用于存储正在进行的请求
const pendingRequests = new Map()

// 生成请求的唯一标识（优化版：处理对象属性顺序问题）
const generateRequestKey = (config) => {
  const { method, url, data, params } = config

  // 标准化对象，确保属性顺序一致
  const normalizeObject = (obj) => {
    if (!obj || typeof obj !== 'object') return ''
    const sortedKeys = Object.keys(obj).sort()
    const normalized = {}
    sortedKeys.forEach(key => {
      normalized[key] = obj[key]
    })
    return JSON.stringify(normalized)
  }

  // 将请求方法和URL作为基础标识
  let key = `${method}:${url}`

  // 添加标准化后的请求参数
  const normalizedParams = normalizeObject(params)
  const normalizedData = normalizeObject(data)

  if (normalizedParams) key += `?${normalizedParams}`
  if (normalizedData) key += `#${normalizedData}`

  return key
}

// 取消重复请求的函数
const cancelPendingRequest = (config) => {
  // 如果配置允许重复请求，则不取消
  if (config.allowDuplicate) return

  const requestKey = generateRequestKey(config)
  if (pendingRequests.has(requestKey)) {
    // 取消之前相同的请求
    const cancelToken = pendingRequests.get(requestKey)
    cancelToken.cancel('请求已被取消，防止重复提交')
    pendingRequests.delete(requestKey)
  }
}

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 取消之前相同的请求（防抖机制）
    cancelPendingRequest(config)

    // 为当前请求创建一个取消令牌
    const source = axios.CancelToken.source()
    config.cancelToken = source.token
    const requestKey = generateRequestKey(config)
    pendingRequests.set(requestKey, source)

    // 设置统一的 Content-Type（避免每次请求都触发OPTIONS）
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json;charset=UTF-8'
    }

    // 请求开始时显示加载状态
    const token = sessionStorage.getItem('token')
    if (token) {
      config.headers.Authorization = token
    }

    // 为特定接口设置超时时间
    if (config.url && config.url.includes('/start/source/file')) {
      config.timeout = 600000 // 10分钟
    }

    // 新增配置项，默认不显示加载状态
    const showLoading = config.showLoading || false
    if (showLoading) {
      loading = ElLoading.service({
        lock: true,
        text: '加载中...',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      // 将加载实例保存到请求配置中
      config.loading = loading
    }
    return config
  },
  (error) => {
    // 请求错误处理
    if (loading) {
      loading.close()
    }
    return Promise.reject(error)
  }
)

// 显示提示信息
const showAlert = (message, display) => {
  // display: 1 表示向用户展示信息，0 表示不展示
  if (display === 1) {
    ElMessageBox.alert(message || '操作失败', '', {
      confirmButtonText: 'OK',
      callback: () => {}
    })
  }
}

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 请求成功处理
    // 从pendingRequests中移除已完成的请求
    const requestKey = generateRequestKey(response.config)
    pendingRequests.delete(requestKey)

    if (response.config.loading) {
      response.config.loading.close()
    }

    const data = response.data

    // 确保数据存在且为对象，避免后续操作崩溃
    if (!data || typeof data !== 'object') {
      console.error('响应数据格式错误:', data)
      return {
        status_code: 500,
        detail: '服务器返回数据格式错误',
        display: 1,
        data: null
      }
    }

    // 确保返回标准格式的响应
    const responseData = {
      status_code: data.status_code || 200,
      detail: data.detail || '',
      display: data.display || 0,
      data: data.data
    }

    // 根据display字段决定是否显示信息
    if (responseData.display === 1) {
      showAlert(responseData.detail, responseData.display)
    }

    // 无论status_code是否为200，都返回resolved promise
    // 业务代码可以根据data.status_code判断请求是否成功
    return responseData
  },
  (error) => {
    // 请求失败处理（网络错误、500错误等）
    console.log(error, 'error>>>>>')

    // 如果是主动取消的请求（防抖机制），则不显示错误提示
    if (axios.isCancel(error)) {
      console.log('请求已被取消:', error.message)
      return Promise.resolve({
        status_code: 400,
        detail: '请求已被取消',
        display: 0,
        data: null
      })
    }

    // 从pendingRequests中移除失败的请求
    if (error.config) {
      const requestKey = generateRequestKey(error.config)
      pendingRequests.delete(requestKey)
    }

    // 关闭loading
    if (error.config && error.config.loading) {
      error.config.loading.close()
    }

    // 构建错误响应对象
    let errorResponse = {
      status_code: 500,
      detail: '服务器内部错误，请联系系统开发人员',
      display: 1,
      data: null
    }

    if (error.response) {
      const { data, status } = error.response

      // 检查是否为标准响应格式
      if (data && typeof data === 'object') {
        errorResponse = {
          status_code: data.status_code || status,
          detail: data.detail || '服务器内部错误',
          display: data.display !== undefined ? data.display : 1,
          data: data.data || null
        }

        // Token失效处理：基于后端返回的402错误码判断
        if (status === 402 || errorResponse.status_code === 402) {
          sessionStorage.removeItem('token')
          ElMessageBox.alert('登录已过期，请重新登录!', '提示', {
            confirmButtonText: '确定',
            callback: () => {
              router.push('/')
            }
          })
          // 返回reject，让页面代码走错误处理分支
          return Promise.reject(errorResponse)
        }
      } else {
        // 非标准格式的错误处理
        errorResponse = {
          status_code: status || 500,
          detail: '服务器返回数据格式错误',
          display: 1,
          data: null
        }
      }
    } else {
      // 网络错误或请求超时
      errorResponse = {
        status_code: 0,
        detail: error.code === 'ECONNABORTED' ? '请求超时，请稍后重试！' : '连接错误，请检查网络连接或服务器是否正常运行！',
        display: 1,
        data: null
      }
    }

    // 根据display字段决定是否显示错误信息
    if (errorResponse.display === 1) {
      showAlert(errorResponse.detail, errorResponse.display)
    }

    // 返回resolved promise，避免错误被全局捕获器捕获，防止页面崩溃
    return Promise.resolve(errorResponse)
  }
)

export default service
